/** Filter sections shown on My Bookings (all tabs). Add or remove entries here. */
export type BookingFilterSection =
    | 'listingTypeIn'
    | 'plans'
    | 'priceGte'
    | 'startDateTimeGte';

export type BookingSortOption =
    | 'newest_to_oldest'
    | 'oldest_to_newest'
    | 'recently_completed';

/** URL/API key for sort selection (not part of bookingFilters sections). */
export const BOOKING_SORT_PARAM_KEY = 'sortBy';

export type BookingStatusFilterOption =
    | 'completed'
    | 'expired'
    | 'declined'
    | 'cancelled';

/** URL/API key for history status checkboxes (not part of bookingFilters sections). */
export const BOOKING_STATUS_FILTER_PARAM_KEY = 'statusFilter';

export const BOOKING_STATUS_FILTER_OPTIONS: {
    value: BookingStatusFilterOption;
    labelKey: string;
}[] = [
    {
        value: 'completed',
        labelKey: 'bookings.filters.status.completed'
    },
    {
        value: 'expired',
        labelKey: 'bookings.filters.status.expired'
    },
    {
        value: 'declined',
        labelKey: 'bookings.filters.status.declined'
    },
    {
        value: 'cancelled',
        labelKey: 'bookings.filters.status.cancelled'
    }
];

export const BOOKING_SORT_OPTIONS: {
    value: BookingSortOption;
    labelKey: string;
}[] = [
    {
        value: 'newest_to_oldest',
        labelKey: 'bookings.filters.sort.newestToOldest'
    },
    {
        value: 'oldest_to_newest',
        labelKey: 'bookings.filters.sort.oldestToNewest'
    },
    {
        value: 'recently_completed',
        labelKey: 'bookings.filters.sort.recentlyCompleted'
    }
];

// All three sort on the booking's checkout date, matching the "Checkout" value
// on the card. `newest_to_oldest`/`oldest_to_newest` used to sort on
// `updated_at` (row last-modified), so picking them reordered the list by
// something the user could not see. `recently_completed` stays distinct
// because it additionally narrows `statusIn` to COMPLETED.
export const BOOKING_SORT_ORDER_BY: Record<BookingSortOption, string> = {
    newest_to_oldest: '-details__end_date_time',
    oldest_to_newest: 'details__end_date_time',
    recently_completed: '-details__end_date_time'
};

export interface BookingFilterConfig {
    filter_name: BookingFilterSection;
    name: string;
    title: string;
    icon: string;
}

export const bookingFilters: BookingFilterConfig[] = [
    {
        filter_name: 'listingTypeIn',
        name: 'filters.type',
        title: 'filters.type',
        icon: '/assets/icons/key.svg'
    },
    {
        filter_name: 'plans',
        name: 'filters.plan',
        title: 'filters.booking',
        icon: '/assets/icons/clock.svg'
    },
    {
        filter_name: 'priceGte',
        name: 'filters.price',
        title: 'filters.price',
        icon: '/assets/icons/card.svg'
    },
    {
        filter_name: 'startDateTimeGte',
        name: 'bookings.filters.dateRange',
        title: 'bookings.filters.dateRange',
        icon: '/assets/icons/clock.svg'
    }
];

export const BOOKING_FILTER_PARAM_KEYS = bookingFilters
    .map((filter) => filter.filter_name)
    .filter((key) => key !== 'priceGte' && key !== 'startDateTimeGte');

export const BOOKING_PRICE_PARAM_KEYS = ['priceGte', 'priceLte'] as const;

export const BOOKING_DATE_PARAM_KEYS = [
    'startDateTimeGte',
    'startDateTimeLte'
] as const;

/** URL/API key for the bookings search box (not counted in filter badge). */
export const BOOKING_SEARCH_PARAM_KEY = 'search';

/** Sent with price filters so the API compares amounts in the selected currency. */
export const BOOKING_CURRENCY_PARAM_KEY = 'currency';

/** Toolbar + filter-modal search config. */
export const bookingSearchFilter = {
    paramKey: BOOKING_SEARCH_PARAM_KEY,
    placeholderKey: 'bookings.searchPlaceholder',
    hintKey: 'bookings.searchHint',
    titleKey: 'bookings.searchFilter'
} as const;

export const BOOKING_QUERY_PARAM_KEYS = [
    BOOKING_SORT_PARAM_KEY,
    BOOKING_STATUS_FILTER_PARAM_KEY,
    ...BOOKING_FILTER_PARAM_KEYS,
    ...BOOKING_PRICE_PARAM_KEYS,
    BOOKING_CURRENCY_PARAM_KEY,
    ...BOOKING_DATE_PARAM_KEYS,
    BOOKING_SEARCH_PARAM_KEY
];

export const BOOKING_PLAN_OPTIONS = [
    {
        labelKey: 'filters.time.hour',
        value: 'hour',
        iconSrc: '/assets/icons/hourly.svg'
    },
    {
        labelKey: 'filters.time.daily',
        value: 'day',
        iconSrc: '/assets/icons/daily.svg'
    },
    {
        labelKey: 'filters.time.montly',
        value: 'month',
        iconSrc: '/assets/icons/monthly.svg'
    },
    {
        labelKey: 'filters.time.annual',
        value: 'annual',
        iconSrc: '/assets/icons/Newannualy.svg'
    }
];
